/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import {
	ISchemaValue,
	ISchemaValueModel,
	SCHEMA_VALUE_TYPE,
	TGenericBlueprintSchema,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import {
	IInspectorControls,
	IInspectorControlValueTypes,
	TInspectorControlComponent,
	TResolvedInspectorControlValueTypes
} from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { getInspectorControls, getInspectorControlValueTypes } from "./inspectorControls";
import { IFieldTypeOption } from "../../Fields/FieldType";
import { getSchemaDefaultIcon } from "../InspectorHelpers";
import { InspectorControl } from "../InspectorControl";

type TModel = ISchemaValueModel<TGenericBlueprintSchema>;

export const ControlValue_ResolveValueTypes: TResolvedInspectorControlValueTypes = (
	schema: ISchemaValue<TGenericBlueprintSchema>
) => {
	const constTypes = getInspectorControlValueTypes(schema.constSchema);

	const types: IFieldTypeOption[] = [];

	if (constTypes.length > 0) {
		types.push(...constTypes);
	} else {
		types.push({
			value: SCHEMA_VALUE_TYPE.CONST,
			icon:
				schema.opts.icon ||
				schema.constSchema.opts.icon ||
				getSchemaDefaultIcon(schema, null) ||
				getSchemaDefaultIcon(schema?.constSchema),
			label: "Constant value"
		});
	}

	if (schema.opts.allowExpression !== false) {
		types.push({
			value: SCHEMA_VALUE_TYPE.EXPRESSION,
			icon: "mdi/function-variant",
			label: "Dynamic value"
		});
	}

	if (schema.opts.allowTranslate !== false && schema.constSchema.name === "constString") {
		types.push({
			value: SCHEMA_VALUE_TYPE.TRANSLATE,
			icon: "mdi/translate",
			label: "Translated value"
		});
	}

	return types;
};

export const ControlValue_ValueTypes: Omit<
	IInspectorControlValueTypes<ISchemaValueModel<TGenericBlueprintSchema>>,
	"model"
> = {
	getCurrentType: (model) => {
		if (model.type === SCHEMA_VALUE_TYPE.CONST) {
			const constControls = getInspectorControls(model.constant);

			if (constControls.valueTypes?.getCurrentType) {
				return constControls.valueTypes.getCurrentType(constControls.valueTypes.model);
			} else {
				return model.type;
			}
		} else {
			return model.type;
		}
	},
	setCurrentType: (model, type) => {
		if (type === SCHEMA_VALUE_TYPE.EXPRESSION || type === SCHEMA_VALUE_TYPE.TRANSLATE) {
			model.schema.setType(model, type as SCHEMA_VALUE_TYPE, true);
		} else {
			model.schema.setType(model, SCHEMA_VALUE_TYPE.CONST, true);

			const constControls = getInspectorControls(model.constant);

			if (constControls.valueTypes?.setCurrentType) {
				constControls.valueTypes.setCurrentType(constControls.valueTypes.model, type);
			}
		}
	}
};

export const ControlValue = (_model: TGenericModelNode): IInspectorControls => {
	const model = _model as TModel;

	const constControls = getInspectorControls(model.constant);

	const headerPrefix =
		model.type === SCHEMA_VALUE_TYPE.CONST && constControls.headerPrefix
			? {
					component: ControlValue_HeaderPrefix,
					model: model
			  }
			: null;

	const headerControls =
		constControls.headerControls && model.type === SCHEMA_VALUE_TYPE.CONST
			? {
					component: ControlValue_HeaderControls,
					model: model
			  }
			: null;

	const primary =
		constControls.primary ||
		model.type === SCHEMA_VALUE_TYPE.EXPRESSION ||
		model.type === SCHEMA_VALUE_TYPE.TRANSLATE
			? {
					component: ControlValue_Primary,
					model: model
			  }
			: null;

	const primaryChildren =
		model.type === SCHEMA_VALUE_TYPE.CONST && constControls.primaryChildren
			? {
					component: ControlValue_PrimaryChildren,
					model: model
			  }
			: null;

	const children =
		model.type === SCHEMA_VALUE_TYPE.CONST && constControls.children
			? {
					component: ControlValue_Children,
					model: model
			  }
			: null;

	return {
		headerPrefix: headerPrefix,
		headerControls: headerControls,
		primary: primary,
		primaryChildren: primaryChildren,
		children: children,
		valueTypes: {
			...ControlValue_ValueTypes,
			model: model
		},
		dependencyModels: [ model.constant, ...(constControls.dependencyModels || []) ],
		displayInSection: constControls?.displayInSection && model.type === SCHEMA_VALUE_TYPE.CONST
	};
};

export const ControlValue_HeaderPrefix: TInspectorControlComponent<TModel> = ({
	model: _model,
	path,
	overrideControls
}) => {
	const { model } = useBlueprintModel(_model, "value");

	const constControls = {
		...getInspectorControls(model?.constant),
		...overrideControls
	};

	const { model: constModel } = useBlueprintModel(model.constant);

	if (!constModel) {
		return null;
	}

	return <InspectorControl config={constControls.headerPrefix} path={path} depth={0} />;
};

export const ControlValue_HeaderControls: TInspectorControlComponent<TModel> = ({
	model: _model,
	path,
	overrideControls
}) => {
	const { model, validationErrors, runtimeErrors } = useBlueprintModel(_model, "value");

	if (!model) {
		return null;
	}

	const constControls = {
		...getInspectorControls(model?.constant),
		...overrideControls
	};

	return (
		<>
			{constControls.headerControls && model.type === SCHEMA_VALUE_TYPE.CONST ? (
				<InspectorControl
					config={constControls.headerControls}
					path={path}
					depth={0}
					additionalValidationErrors={validationErrors.concat(runtimeErrors)}
				/>
			) : null}
		</>
	);
};

export const ControlValue_Primary: TInspectorControlComponent<TModel> = ({
	model: _model,
	path,
	depth,
	overrideControls,
	id,
	label
}) => {
	const { model, validationErrors, runtimeErrors } = useBlueprintModel(_model, "value");

	if (!model) {
		return null;
	}

	const constControls = {
		...getInspectorControls(model.constant),
		...overrideControls
	};

	const expressionControls = getInspectorControls(model.expression);
	const translateControls = getInspectorControls(model.translate);

	if (model.type === SCHEMA_VALUE_TYPE.CONST) {
		return (
			<InspectorControl
				id={id}
				label={label}
				config={constControls.primary}
				path={path}
				depth={depth}
				additionalValidationErrors={validationErrors.concat(runtimeErrors)}
			/>
		);
	} else if (model.type === SCHEMA_VALUE_TYPE.EXPRESSION) {
		return (
			<InspectorControl
				id={id}
				label={label}
				config={expressionControls.primary}
				path={path}
				depth={depth}
				additionalValidationErrors={validationErrors.concat(runtimeErrors)}
			/>
		);
	} else if (model.type === SCHEMA_VALUE_TYPE.TRANSLATE) {
		return (
			<InspectorControl
				id={id}
				label={label}
				config={translateControls.primary}
				path={path}
				depth={depth}
				additionalValidationErrors={validationErrors.concat(runtimeErrors)}
			/>
		);
	} else {
		return null;
	}
};

export const ControlValue_PrimaryChildren: TInspectorControlComponent<TModel> = ({
	model: _model,
	path,
	depth,
	overrideControls,
	id,
	label
}) => {
	const { model, validationErrors, runtimeErrors } = useBlueprintModel(_model, "value");
	useBlueprintModel(model?.constant);

	if (!model) {
		return null;
	}

	const constControls = {
		...getInspectorControls(model?.constant),
		...overrideControls
	};

	return (
		<InspectorControl
			id={id}
			label={label}
			config={constControls.primaryChildren}
			path={path}
			depth={depth}
			additionalValidationErrors={validationErrors.concat(runtimeErrors)}
		/>
	);
};

export const ControlValue_Children: TInspectorControlComponent<TModel> = ({
	model: _model,
	path,
	depth,
	overrideControls,
	id,
	label
}) => {
	const { model, validationErrors, runtimeErrors } = useBlueprintModel(_model, "value");
	useBlueprintModel(model?.constant);

	if (!model) {
		return null;
	}

	const constControls = {
		...getInspectorControls(model?.constant),
		...overrideControls
	};

	return (
		<InspectorControl
			id={id}
			label={label}
			config={constControls.children}
			path={path}
			depth={depth}
			additionalValidationErrors={validationErrors.concat(runtimeErrors)}
		/>
	);
};
